import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';

const Canvas = styled.canvas`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
`;

const INTENSITY_CONFIG = {
    low: { particles: 40, speedMultiplier: 0.7 },
    medium: { particles: 80, speedMultiplier: 1 },
    high: { particles: 150, speedMultiplier: 1.3 }
};

const CONFETTI_COLORS = [
    '#ff6b8b',
    '#6b5bff',
    '#4ecdc4',
    '#ffd93d',
    '#ff8c42',
    '#98d8c8'
];

const ConfettiEffect = ({ intensity = 'medium' }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const particlesRef = useRef([]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const config = INTENSITY_CONFIG[intensity] || INTENSITY_CONFIG.medium;

        const resize = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const createParticles = () => {
            particlesRef.current = [];
            for (let i = 0; i < config.particles; i++) {
                const type = Math.random() > 0.5 ? 'rect' : 'ribbon';
                particlesRef.current.push({
                    x: Math.random() * canvas.width,
                    y: -20 - Math.random() * canvas.height,
                    width: type === 'rect' ? Math.random() * 8 + 4 : Math.random() * 4 + 2,
                    height: type === 'rect' ? Math.random() * 8 + 4 : Math.random() * 20 + 10,
                    type,
                    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
                    speed: (Math.random() * 2 + 1) * config.speedMultiplier,
                    rotation: Math.random() * Math.PI * 2,
                    rotationSpeed: (Math.random() - 0.5) * 0.1,
                    wobble: Math.random() * Math.PI * 2,
                    wobbleSpeed: Math.random() * 0.1
                });
            }
        };
        createParticles();

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            particlesRef.current.forEach(particle => {
                ctx.save();
                ctx.translate(
                    particle.x + Math.sin(particle.wobble) * 30,
                    particle.y
                );
                ctx.rotate(particle.rotation);

                ctx.fillStyle = particle.color;

                if (particle.type === 'rect') {
                    ctx.fillRect(
                        -particle.width / 2,
                        -particle.height / 2,
                        particle.width,
                        particle.height
                    );
                } else {
                    ctx.beginPath();
                    ctx.moveTo(0, -particle.height / 2);
                    ctx.quadraticCurveTo(
                        particle.width,
                        0,
                        0,
                        particle.height / 2
                    );
                    ctx.lineWidth = particle.width;
                    ctx.strokeStyle = particle.color;
                    ctx.stroke();
                }

                ctx.restore();

                particle.y += particle.speed;
                particle.rotation += particle.rotationSpeed;
                particle.wobble += particle.wobbleSpeed;

                if (particle.y > canvas.height + 30) {
                    particle.y = -30;
                    particle.x = Math.random() * canvas.width;
                }
            });

            animationRef.current = requestAnimationFrame(animate);
        };
        animate();

        return () => {
            window.removeEventListener('resize', resize);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [intensity]);

    return <Canvas ref={canvasRef} />;
};

export default ConfettiEffect;
